import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import { Field, Text, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import * as React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import {
  StHeadingContainer,
  StHeadingText,
  StModalContainerWithModal,
  StmodalContainer,
} from '../../shared/CustomModal/CustomModal.styles';
import { RegisterMergeRichText, RegisterMergeText } from '../RegistrationMergeFields';
import axios from 'axios';
import { getCookie } from '../../../lib/utils/enrollmentUtil';
import ButtonDiscreet from '../../shared/Button/ButtonDiscreet';
import ButtonPrimary from '../../shared/Button/PrimaryButton';
import { getValidateCrEmailIdURL } from '../../../lib/utils/common';
import EmailAutoSugest from 'src/components/shared/EmailAutocomplete';
import { getContactEmailDomainsListForContactInfo } from 'src/GraphQL/queries/contactEmailDomainsList.query';
import { IItemWithString } from 'lib/type/common';

interface IProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onConfirm: (email: string) => void;
  updatePopUpEmailLabel: Field<string>;
  updatePopUpSubTitle: Field<string>;
  updatePopUpTitle: Field<string>;
  confirmButtonText: Field<string>;
  cancelButtonnText: Field<string>;
  emailInvalidErrorMessage: Field<string>;
  sameEmailErrorMessage?: Field<string>;
  emailAlreadyExistErrorMessage?: Field<string>;
}
const validateEmail = (email: string) => {
  const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\.,;:\s@"]+\.)+[^<>()[\]\.,;:\s@"]{2,}\s*)$/;
  return emailRegex.test(email);
};

const validateEmailServerSide = async (email: string, siteSettings: any) => {
  try {
    const validateEmailUrl = getValidateCrEmailIdURL(
      email,
      getCookie('bx_guest_ref'),
      siteSettings?.source?.fields?.name?.value || 'CR',
      true
    );
    const response = await axios.get(validateEmailUrl);
    if (response?.data?.isEmailExist) {
      return true;
    }
    return false;
  } catch {
    return true;
  }
};

const UpdateEmailDialog: React.FC<IProps> = (props) => {
  const crEmail = useSelector((state: RootState) => state.registrationDetails.registration.email);
  const myProfileData = useSelector((state: RootState) => state.crProfile.profileData);
  const emailFromProfile = myProfileData?.basicDetails?.email;
  const { sitecoreContext } = useSitecoreContext();
  const siteSettings: any = sitecoreContext?.siteSettings;
  const language = sitecoreContext?.language || process.env.DEFAULT_LANGUAGE || 'en';
  const emailId = crEmail || emailFromProfile || '';
  const { open, setOpen } = props;
  const confirmText = props.confirmButtonText;
  const cancelText = props.cancelButtonnText;
  const heading = props.updatePopUpTitle;
  const description = props.updatePopUpSubTitle;
  const inputLabel = props.updatePopUpEmailLabel;
  const emailInvalidErrorMessage = props.emailInvalidErrorMessage;
  const sameEmailErrorMessage = props.sameEmailErrorMessage || {
    value: 'Please enter a different email address.',
  };
  const emailExistsErrorMessage = props?.emailAlreadyExistErrorMessage || {
    value: 'Email already exists',
  };

  const {
    control,
    formState: { errors, isValid },
    reset,
    handleSubmit,
  } = useForm<{ email: string }>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: { email: '' },
  });
  const [emailDomains, setEmailDomains] = React.useState<IItemWithString[]>([]);
  const updatedEmailRef = React.useRef('');
  const [isSameEmailId, setIsSameEMailId] = React.useState(false);
  const [isEmailExists, setIsEmailExists] = React.useState(false);

  React.useEffect(() => {
    void (async () => {
      try {
        if (emailDomains?.length > 0) {
          return;
        }
        const list = await getContactEmailDomainsListForContactInfo(language, siteSettings);

        setEmailDomains(list);
      } catch {}
    })();
  }, [siteSettings]);

  React.useEffect(() => {
    if (open) {
      reset({ email: '' });
      updatedEmailRef.current = '';
      setIsSameEMailId(false);
      setIsEmailExists(false);
    }
  }, [open, reset]);

  const onSubmit = async (data: { email: string }) => {
    const updatedEmail = updatedEmailRef.current || data.email;
    if (updatedEmail === emailId) {
      setIsSameEMailId(true);
      return;
    }
    const exists = await validateEmailServerSide(updatedEmail, siteSettings);
    if (exists) {
      setIsEmailExists(true);
      return;
    }
    props.onConfirm(updatedEmail);
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        setIsSameEMailId(false);
        setIsEmailExists(false);
        setOpen(false);
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      id="update-email-dialog"
      className="ui-update-email-dialog"
    >
      <StModalContainerWithModal>
        <StmodalContainer
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2rem',
          }}
          className="ui-update-email-dialog__container"
        >
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <StHeadingContainer>
              <StHeadingText id="modal-modal-title" variant="h6">
                <RegisterMergeText field={heading} />
              </StHeadingText>
            </StHeadingContainer>
            <Box
              sx={{
                width: '100%',
                paddingTop: '1.5rem',
              }}
            >
              <Box>
                <RegisterMergeRichText field={description} />
              </Box>
              <Box display={'flex'} flexDirection={'column'} paddingTop={'2rem'} gap={1}>
                <RegisterMergeText field={inputLabel} />
                <Controller
                  control={control}
                  name="email"
                  rules={{
                    required: {
                      value: true,
                      message: emailInvalidErrorMessage?.value || '',
                    },
                    validate: async (value) => {
                      return validateEmail(value) ? true : (emailInvalidErrorMessage?.value ?? '');
                    },
                  }}
                  render={({ field }) => (
                    <EmailAutoSugest
                      emailDomains={emailDomains}
                      fieldProps={field}
                      inputId="update-email-input"
                      onLightBackground
                      updateEmailDialogField
                    />
                  )}
                />

                {(isSameEmailId || isEmailExists || errors?.email) && (
                  <Typography color={'red'}>
                    <Text
                      field={
                        isSameEmailId
                          ? sameEmailErrorMessage
                          : isEmailExists
                            ? emailExistsErrorMessage
                            : emailInvalidErrorMessage
                      }
                    />
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              display="flex"
              justifyContent="flex-end"
              columnGap="1rem"
              sx={{ marginTop: '2rem' }}
            >
              <ButtonDiscreet
                type="button"
                onClick={() => {
                  setIsSameEMailId(false);
                  setIsEmailExists(false);
                  setOpen(false);
                }}
              >
                {cancelText.value}
              </ButtonDiscreet>
              <ButtonPrimary type="submit" disabled={!isValid}>
                {confirmText.value}
              </ButtonPrimary>
            </Box>
          </Box>
        </StmodalContainer>
      </StModalContainerWithModal>
    </Modal>
  );
};

export default UpdateEmailDialog;
